import { Activity, AlertTriangle, Boxes, CircleUserRound, ClipboardCheck, ClipboardList, CloudCog, LayoutDashboard, LogOut, Plus, RefreshCw, ShoppingCart, Users, Wifi, WifiOff } from 'lucide-react'
import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { Button } from '../common/Button'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { useAuth } from '../../features/auth/auth-context'
import { useOffline } from '../../services/offline/offline-context'
import { hasAnyPermission, hasPermission } from '../../features/auth/permissions'
import { SyncPendingDialog } from './SyncPendingDialog'

export function AppLayout() {
  const { user, signOut } = useAuth()
  const offline = useOffline()
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [showPending, setShowPending] = useState(false)
  const location = useLocation()
  const role = user?.profile.role
  const modeLabel = role === 'REQUISITANTE' ? 'Modo Requisitante' : role === 'COMPRADOR' ? 'Modo Comprador' : 'Modo Administrador'

  const online = offline.connectivity === 'ONLINE'
  const connectionLabel = offline.syncing ? 'Sincronizando' : offline.connectivity === 'OFFLINE' ? 'Offline' : offline.connectivity === 'DEGRADED' ? 'Conexão instável' : 'Online'

  async function requestSignOut() {
    if (offline.pendingCount + offline.attentionCount > 0) {
      setConfirmLogout(true)
      return
    }
    await signOut()
  }

  return (
    <div className="app-shell">
      <header className="app-header no-print">
        <div className="app-brand">
          <span className="app-brand-mark">RM</span>
          <div>
            <strong>Requisição de Materiais</strong>
            <span>{modeLabel}</span>
          </div>
        </div>
        <nav aria-label="Navegação principal">
          {hasAnyPermission(user, ['requisitions.view_own', 'requisitions.view_all']) ? <>
            <NavLink to="/app/requisicoes" className={({ isActive }) => isActive && !location.pathname.endsWith('/nova') ? 'active' : ''}>
              <ClipboardList aria-hidden="true" /> Requisições
            </NavLink>
            {hasPermission(user, 'requisitions.create') ? <NavLink to="/app/requisicoes/nova"><Plus aria-hidden="true" /> {role === 'ADMIN' ? 'Nova Requisição' : 'Nova'}</NavLink> : null}
          </> : null}
          {hasPermission(user, 'purchases.view') ? <NavLink to="/app/compras">
            <ShoppingCart aria-hidden="true" /> Compras
          </NavLink> : null}
          {hasAnyPermission(user, ['users.view', 'catalog.manage', 'item_requests.manage', 'audit.view']) ? <>
            <NavLink end to="/app/admin"><LayoutDashboard aria-hidden="true" /> Administração</NavLink>
            {hasPermission(user, 'users.view') ? <NavLink to="/app/admin/usuarios"><Users aria-hidden="true" /> Usuários</NavLink> : null}
            {hasPermission(user, 'catalog.manage') ? <NavLink end to="/app/admin/itens"><Boxes aria-hidden="true" /> Itens</NavLink> : null}
            {hasPermission(user, 'item_requests.manage') ? <NavLink to="/app/admin/itens/solicitacoes"><ClipboardCheck aria-hidden="true" /> Pendências</NavLink> : null}
            {hasPermission(user, 'audit.view') ? <NavLink to="/app/admin/auditoria"><Activity aria-hidden="true" /> Auditoria</NavLink> : null}
          </> : null}
        </nav>
        <div className="session-tools">
          <span className={`online-state ${online ? 'is-online' : 'is-offline'}`} title={connectionLabel}>
            {offline.syncing ? <CloudCog aria-hidden="true" /> : online ? <Wifi aria-hidden="true" /> : <WifiOff aria-hidden="true" />}
            {connectionLabel}
          </span>
          {offline.pendingCount ? <button type="button" className="sync-count" onClick={() => setShowPending(true)}>{offline.pendingCount} aguardando envio</button> : null}
          {offline.attentionCount ? <button type="button" className="sync-attention" onClick={() => setShowPending(true)}><AlertTriangle />{offline.attentionCount} para revisar</button> : null}
          <NavLink className="session-user" to="/app/minha-conta" title="Minha conta"><CircleUserRound aria-hidden="true" />{user?.profile.nome}</NavLink>
          <button className="icon-button" type="button" aria-label="Sair" title="Sair" onClick={() => void requestSignOut()}><LogOut /></button>
        </div>
      </header>
      {offline.updateAvailable ? <aside className="update-banner no-print" role="status"><span>Nova versão disponível.</span><Button icon={<RefreshCw />} disabled={offline.hasActiveDraft} onClick={() => void offline.applyUpdate()}>{offline.hasActiveDraft ? 'Conclua a edição para atualizar' : 'Atualizar'}</Button></aside> : null}
      <main className="app-content"><Outlet /></main>
      <ConfirmDialog
        open={confirmLogout}
        title="Sair com alterações pendentes?"
        description="Existem alterações ainda não enviadas. Elas permanecerão protegidas neste dispositivo e só serão retomadas quando este mesmo usuário entrar novamente."
        confirmLabel="Sair mesmo assim"
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => { setConfirmLogout(false); void signOut() }}
      />
      <SyncPendingDialog
        open={showPending}
        operations={offline.pendingOperations}
        syncing={offline.syncing}
        onClose={() => setShowPending(false)}
        onRetry={offline.retryPending}
        onSync={offline.syncNow}
      />
    </div>
  )
}
